'use client'

import { useState } from 'react'
import { Card, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { MoreVertical, Copy, Trash2, Pencil, ExternalLink, Grid3x3 } from 'lucide-react'
import Image from 'next/image'
import { cn } from '@/lib/utils'
import { useLanguage } from '@/lib/i18n/language-provider'

interface SynopticProject {
  id: string
  name: string
  description?: string
  thumbnail_url?: string
  updated_at: string
}

interface ProjectCardProps {
  project: SynopticProject
  onOpen: (projectId: string) => void
  onRename?: (projectId: string, currentName: string) => void
  onDuplicate?: (projectId: string, currentName: string) => void
  onDelete?: (projectId: string, projectName: string) => void
  className?: string
  showActions?: boolean // Afficher ou non le menu d'actions
}

export function ProjectCard({
  project,
  onOpen,
  onRename,
  onDuplicate,
  onDelete,
  className,
  showActions = true
}: ProjectCardProps) {
  const { t, language } = useLanguage()
  const [isHovered, setIsHovered] = useState(false)
  
  const handleCardClick = () => {
    onOpen(project.id)
  }
  
  return (
    <Card
      className={cn(
        'group cursor-pointer transition-all duration-200',
        'hover:border-primary hover:shadow-lg',
        'active:scale-[0.98]',
        className
      )}
      onClick={handleCardClick}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <CardContent className="p-4">
        {/* Thumbnail */}
        <div className="aspect-video bg-muted rounded-md mb-3 overflow-hidden relative">
          {project.thumbnail_url ? (
            <Image
              src={project.thumbnail_url}
              alt={project.name}
              fill
              className="object-cover transition-transform duration-200 group-hover:scale-105"
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center text-muted-foreground bg-gradient-to-br from-muted to-muted/50">
              <Grid3x3 className="h-12 w-12 opacity-50" />
            </div>
          )}
          
          {/* Overlay au survol */}
          {isHovered && (
            <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
              <Button
                variant="secondary"
                size="sm"
                className="gap-2"
                onClick={handleCardClick}
              >
                <ExternalLink className="h-4 w-4" />
                {t("synoptic.card.open")}
              </Button>
            </div>
          )}
        </div>
        
        {/* Title + Actions */}
        <div className="flex items-start justify-between gap-2">
          <div className="flex-1 min-w-0">
            <h3 className="font-semibold text-base mb-1 break-words line-clamp-2">
              {project.name}
            </h3>
            <p className="text-xs text-muted-foreground">
              {t("synoptic.card.modified")} {formatRelativeTime(project.updated_at, language, t)}
            </p>
          </div>
          
          {/* Menu actions */}
          {showActions && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-8 w-8 p-0 opacity-0 group-hover:opacity-100 transition-opacity"
                >
                  <MoreVertical className="h-4 w-4" />
                  <span className="sr-only">{t("synoptic.card.actions")}</span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                {onRename && (
                  <DropdownMenuItem
                    onClick={(e) => {
                      e.stopPropagation()
                      onRename(project.id, project.name)
                    }}
                  >
                    <Pencil className="h-4 w-4 mr-2" />
                    {t("synoptic.card.rename")}
                  </DropdownMenuItem>
                )}
                {onDuplicate && (
                  <DropdownMenuItem
                    onClick={(e) => {
                      e.stopPropagation()
                      onDuplicate(project.id, project.name)
                    }}
                  >
                    <Copy className="h-4 w-4 mr-2" />
                    {t("synoptic.card.duplicate")}
                  </DropdownMenuItem>
                )}
                {onDelete && (
                  <>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem
                      onClick={(e) => {
                        e.stopPropagation()
                        onDelete(project.id, project.name)
                      }}
                      className="text-destructive focus:text-destructive"
                    >
                      <Trash2 className="h-4 w-4 mr-2" />
                      {t("synoptic.card.delete")}
                    </DropdownMenuItem>
                  </>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>
        
        {/* Description (optionnel) */}
        {project.description && (
          <p className="text-sm text-muted-foreground mt-2 line-clamp-2">
            {project.description}
          </p>
        )}
      </CardContent>
    </Card>
  )
}

/**
 * Formatte une date ISO en texte relatif
 */
function formatRelativeTime(isoDate: string, language: 'fr' | 'en', t: (key: string, variables?: Record<string, string | number>) => string): string {
  const date = new Date(isoDate)
  const now = new Date()
  const diffMs = now.getTime() - date.getTime()
  const diffSeconds = Math.floor(diffMs / 1000)
  const diffMinutes = Math.floor(diffSeconds / 60)
  const diffHours = Math.floor(diffMinutes / 60)
  const diffDays = Math.floor(diffHours / 24)
  
  if (diffSeconds < 60) return t("time.now")
  if (diffMinutes < 60) return t("time.minutesAgo", { n: diffMinutes })
  if (diffHours < 24) return t("time.hoursAgo", { n: diffHours })
  if (diffDays < 7) return t("time.daysAgo", { n: diffDays })
  
  const locale = language === 'fr' ? 'fr-FR' : 'en-US'
  return date.toLocaleDateString(locale, { 
    day: 'numeric', 
    month: 'short',
    year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined
  })
}

